import { PlusCircledIcon } from "@radix-ui/react-icons";
import { CheckIcon, X } from "lucide-react";

import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

// Define the value type more clearly
export type OptionValue = string | { [key: string]: string };

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps {
  /**
   * An array of option objects to be displayed in the multi-select component.
   * Each option object has a label, value, and an optional icon.
   */
  options: {
    /** The text to display for the option. */
    label: string;
    /** The unique value associated with the option. */
    value: OptionValue;
    /** Optional icon component to display alongside the option. */
    icon?: React.ComponentType<{ className?: string }>;
    /** Optional description text to display below the label. */
    description?: string;
  }[];

  /**
   * Selection mode - determines if component allows single or multiple selections
   * Optional, defaults to "multiple"
   */
  mode?: "single" | "multiple";

  /**
   * Callback function triggered when the selected values change.
   * Returns array of original values (preserving string vs object types)
   */
  onValueChange: (value: OptionValue[]) => void;

  /** The default selected values when the component mounts. */
  defaultValue?: OptionValue[];

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string;

  /**
   * An array of values that should be disabled and unselectable.
   * Optional, defaults to an empty array.
   */
  disabledValues?: OptionValue[];

  /**
   * Maximum number of items to display. Extra selected items will be summarized.
   * Optional, defaults to 3.
   */
  maxCount?: number;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * If true, renders the multi-select component as a child of another component.
   * Optional, defaults to false.
   */
  asChild?: boolean;

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string;

  icon?: React.ElementType;

  // Button props we want to support
  disabled?: boolean;
  id?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** Show a clear button on the trigger when selections exist (use for filter UIs) */
  enableClear?: boolean;
}

// Helper function to compare values (strings or objects)
const isValueEqual = (a: OptionValue, b: OptionValue): boolean => {
  if (typeof a === "string" && typeof b === "string") {
    return a === b;
  }

  // Compare objects
  if (
    typeof a === "object" &&
    typeof b === "object" &&
    a !== null &&
    b !== null
  ) {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((key) => a[key] === b[key]);
  }

  return false;
};

// Helper function to convert value to string for keys/comparison
const valueToString = (value: OptionValue): string => {
  if (typeof value === "string") return value;
  return JSON.stringify(value);
};

const _MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      onValueChange,
      defaultValue = [],
      placeholder = "",
      disabledValues = [],
      modalPopover = false,
      icon: Icon = PlusCircledIcon,
      className,
      mode = "multiple",
      disabled,
      id,
      onClick,
      enableClear = false,
      // Treat this as a filter control if the caller passes a className that includes a known filter wrapper
    },
    ref,
  ) => {
    const { t } = useTranslation();

    // Store original values, not stringified versions
    const [selectedValues, setSelectedValues] =
      React.useState<OptionValue[]>(defaultValue);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

    React.useEffect(() => {
      setSelectedValues(defaultValue);
    }, [defaultValue]);

    const handleInputKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>,
    ) => {
      if (event.key === "Enter") {
        setIsPopoverOpen(true);
      } else if (event.key === "Backspace" && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const toggleOption = (value: OptionValue) => {
      let newSelectedValues: OptionValue[];

      if (mode === "single") {
        const isCurrentlySelected = selectedValues.some((selected) =>
          isValueEqual(selected, value),
        );
        newSelectedValues = isCurrentlySelected ? [] : [value];
        setIsPopoverOpen(false);
      } else {
        const isCurrentlySelected = selectedValues.some((selected) =>
          isValueEqual(selected, value),
        );
        newSelectedValues = isCurrentlySelected
          ? selectedValues.filter((selected) => !isValueEqual(selected, value))
          : [...selectedValues, value];
      }

      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const handleClear = () => {
      setSelectedValues([]);
      onValueChange([]);
    };

    const toggleAll = () => {
      if (selectedValues.length === options.length) {
        handleClear();
      } else {
        const allValues = options.map((option) => option.value);
        setSelectedValues(allValues);
        onValueChange(allValues);
      }
    };

    const isOptionSelected = (optionValue: OptionValue): boolean => {
      return selectedValues.some((selected) =>
        isValueEqual(selected, optionValue),
      );
    };

    const isOptionDisabled = (optionValue: OptionValue): boolean => {
      return disabledValues.some((disabledValue) =>
        isValueEqual(disabledValue, optionValue),
      );
    };

    return (
      <Popover
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        modal={modalPopover}
      >
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant="outline"
            className={cn(
              "w-full justify-start",
              selectedValues.length === 0 ? "text-muted-foreground" : undefined,
              className,
            )}
            disabled={disabled}
            id={id}
            onClick={onClick}
          >
            {Icon && <Icon className="mr-2 h-4 w-4" />}
            {placeholder}
            {selectedValues.length > 0 && (
              <>
                <Separator orientation="vertical" className="mx-2 h-4" />
                {mode === "single" ? (
                  <Badge
                    variant="secondary"
                    className="rounded-sm px-1 font-normal"
                  >
                    {
                      options.find((option) =>
                        isValueEqual(option.value, selectedValues[0]),
                      )?.label
                    }
                  </Badge>
                ) : (
                  <>
                    <Badge
                      variant="secondary"
                      className="rounded-sm px-1 font-normal lg:hidden"
                    >
                      {selectedValues.length}
                    </Badge>
                    <div className="hidden lg:flex gap-1">
                      {selectedValues.length > 2 ? (
                        <Badge
                          variant="secondary"
                          className="rounded-sm px-1 font-normal"
                        >
                          {selectedValues.length} {t("selected")}
                        </Badge>
                      ) : (
                        options
                          .filter((option) => isOptionSelected(option.value))
                          .map((option) => {
                            if (option.icon) {
                              return (
                                <Badge
                                  variant="secondary"
                                  key={valueToString(option.value)}
                                  className="rounded-sm px-0 font-normal"
                                >
                                  <option.icon />
                                </Badge>
                              );
                            }
                            return (
                              <Badge
                                variant="secondary"
                                key={valueToString(option.value)}
                                className="rounded-sm px-0 font-normal"
                              >
                                {option.label}
                              </Badge>
                            );
                          })
                      )}
                    </div>
                  </>
                )}
                {enableClear && (
                  <>
                    <Separator orientation="vertical" className="mx-2 h-4" />
                    <X
                      className="w-4 h-4 opacity-50 hover:opacity-100 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClear();
                      }}
                    />
                  </>
                )}
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-auto min-w-[250px] z-[60]"
          align="start"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command>
            <CommandInput
              placeholder="Suchen..."
              onKeyDown={handleInputKeyDown}
            />
            <CommandList>
              <CommandEmpty>Keine Ergebnisse.</CommandEmpty>
              <CommandGroup>
                {mode === "multiple" && (
                  <CommandItem
                    key="all"
                    onSelect={toggleAll}
                    className="cursor-pointer"
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                        selectedValues.length === options.length
                          ? "bg-primary text-primary-foreground"
                          : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <CheckIcon className="h-4 w-4" />
                    </div>
                    <span>{t("select_all")}</span>
                  </CommandItem>
                )}
                {options.map((option) => {
                  const isSelected = isOptionSelected(option.value);

                  return (
                    <CommandItem
                      key={valueToString(option.value)}
                      value={option.label}
                      onSelect={() => toggleOption(option.value)}
                      className="cursor-pointer"
                      disabled={isOptionDisabled(option.value)}
                    >
                      <div
                        className={cn(
                          "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50 [&_svg]:invisible",
                        )}
                      >
                        <CheckIcon className="h-4 w-4" />
                      </div>
                      {option.icon && <option.icon />}
                      <div className="flex flex-col">
                        <span>{option.label}</span>
                        {option.description && (
                          <span className="text-xs text-muted-foreground">
                            {option.description}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <div className="flex items-center justify-between">
                  {selectedValues.length > 0 && (
                    <>
                      <CommandItem
                        onSelect={handleClear}
                        className="flex-1 justify-center cursor-pointer"
                      >
                        {t("remove_filter")}
                      </CommandItem>
                      <Separator
                        orientation="vertical"
                        className="flex min-h-6 h-full"
                      />
                    </>
                  )}
                  <CommandItem
                    onSelect={() => setIsPopoverOpen(false)}
                    className="flex-1 justify-center cursor-pointer max-w-full"
                  >
                    <span className="capitalize">{t("close")}</span>
                  </CommandItem>
                </div>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);

_MultiSelect.displayName = "MultiSelect";

export const MultiSelect = React.memo(_MultiSelect);
